import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { Observable } from 'rxjs';
import {
  MiniShoppingListSearchResult,
  MiniShoppingListsSearchParams,
  PrincipalSharedShoppingListFlagsPage,
  PrincipalSharedShoppingListSearchParams,
  ShoppingList,
  ShoppingListPermission,
  UpdateShoppingListPermissionsParams,
  WritableShoppingListSearchResult,
  WritableShoppingListsSearchParams,
} from '../../model';
import { Occ } from '../../occ/occ-models';
import { ConverterService } from '../../util';
import { PRINCIPALS_NORMALIZER, SHOPPING_LIST_NORMALIZER, SHOPPING_LIST_PERMISSIONS_SERIALIZER } from './converters';

@Injectable({
  providedIn: 'root',
})
export class ShoppingListConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  search(userId: string, searchParams: MiniShoppingListsSearchParams): Observable<MiniShoppingListSearchResult> {
    return this.httpClient.get<MiniShoppingListSearchResult>(
      this.occEndpointsService.buildUrl('shoppingLists', {
        urlParams: { userId: userId },
        queryParams: {
          fields: 'FULL',
          page: searchParams?.page ?? 0,
          count: searchParams?.count ?? 10,
          name: searchParams?.name,
          type: searchParams?.accessType || undefined,
        },
      })
    );
  }

  searchWritable(userId: string, searchParams: WritableShoppingListsSearchParams): Observable<WritableShoppingListSearchResult> {
    return this.httpClient.get<WritableShoppingListSearchResult>(
      this.occEndpointsService.buildUrl('writableShoppingLists', {
        urlParams: { userId: userId },
        queryParams: {
          page: searchParams?.page ?? 0,
          count: searchParams?.count ?? 10,
          name: searchParams?.name,
        },
      })
    );
  }

  load(userId: string, code: string): Observable<ShoppingList> {
    return this.httpClient
      .get<Occ.CartTemplate>(
        this.occEndpointsService.buildUrl('shoppingList', {
          urlParams: {
            userId: userId,
            shoppingListCode: code,
          },
        })
      )
      .pipe(this.converterService.pipeable(SHOPPING_LIST_NORMALIZER));
  }

  updateName(userId: string, code: string, name: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.httpClient.put(
      this.occEndpointsService.buildUrl('shoppingListName', {
        urlParams: {
          userId: userId,
          shoppingListCode: code,
        },
        queryParams: {
          newTemplateName: name,
        },
      }),
      {},
      { headers }
    );
  }

  updateDescription(userId: string, code: string, description: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.httpClient.put(
      this.occEndpointsService.buildUrl('shoppingListDescription', {
        urlParams: {
          userId: userId,
          shoppingListCode: code,
        },
        queryParams: {
          newShoppingListDescription: description,
        },
      }),
      {},
      { headers }
    );
  }

  updateContactPerson(userId: string, code: string, customerId: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.httpClient.put(
      this.occEndpointsService.buildUrl('shoppingListContactPerson', {
        urlParams: {
          userId: userId,
          shoppingListCode: code,
          customerId,
        },
      }),
      {},
      { headers }
    );
  }

  delete(userId: string, code: string): Observable<any> {
    return this.httpClient.delete(
      this.occEndpointsService.buildUrl('shoppingList', {
        urlParams: {
          userId: userId,
          shoppingListCode: code,
        },
      })
    );
  }

  create(userId: string, cartCode?: string, cloneCode?: string, name?: string, description?: string): Observable<ShoppingList> {
    let url;
    if (cartCode) {
      url = this.occEndpointsService.buildUrl('shoppingLists', {
        urlParams: {
          userId: userId,
        },
        queryParams: {
          cartCode,
          templateName: name || '',
          description: description,
        },
      });
    } else if (cloneCode) {
      url = this.occEndpointsService.buildUrl('shoppingLists', {
        urlParams: {
          userId: userId,
        },
        queryParams: {
          templateCode: cloneCode,
          templateName: name || '',
          description: description,
        },
      });
    } else {
      url = this.occEndpointsService.buildUrl('shoppingLists', {
        urlParams: {
          userId: userId,
        },
        queryParams: {
          templateName: name || '',
          description: description,
        },
      });
    }
    return this.httpClient.post<Occ.CartTemplate>(url, {}).pipe(this.converterService.pipeable(SHOPPING_LIST_NORMALIZER));
  }

  updatePermissions(
    userId: string,
    code: string,
    permissions: ShoppingListPermission[],
    params?: UpdateShoppingListPermissionsParams
  ): Observable<any> {
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('shoppingListPrincipals', {
        urlParams: {
          userId: userId,
          shoppingListCode: code,
        },
        queryParams: {
          isEveryoneReads: params?.isEveryoneReads,
          isEveryoneWrites: params?.isEveryoneWrites,
        },
      }),
      this.converterService.convert(permissions, SHOPPING_LIST_PERMISSIONS_SERIALIZER)
    );
  }

  getPrincipals(
    userId: string,
    code: string,
    searchParams: PrincipalSharedShoppingListSearchParams
  ): Observable<PrincipalSharedShoppingListFlagsPage> {
    const url = this.occEndpointsService.buildUrl('shoppingListPrincipals', {
      urlParams: { userId: userId, shoppingListCode: code },
      queryParams: {
        page: searchParams.page,
        count: searchParams.count,
        searchTerm: searchParams.searchTerm || '',
        sort: searchParams.sort,
      },
    });
    return this.httpClient
      .get<Occ.PrincipalSharedCartTemplateFlagsPage>(url)
      .pipe(this.converterService.pipeable(PRINCIPALS_NORMALIZER));
  }
}
